import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/errors";
import { format } from "date-fns";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TrendingUp, TrendingDown, Plus, CalendarIcon, History, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUserPreferences } from "@/hooks/useUserPreferences";

interface BalanceSnapshot {
  id: string;
  balance: number;
  recorded_at: string;
  notes: string | null;
}

interface BalanceHistoryProps {
  accountId?: string;
  walletId?: string;
  accountName: string;
  currentBalance: number;
  currencySymbol: string;
}

export const BalanceHistory = ({ 
  accountId, 
  walletId, 
  accountName, 
  currentBalance,
  currencySymbol 
}: BalanceHistoryProps) => {
  const [snapshots, setSnapshots] = useState<BalanceSnapshot[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newBalance, setNewBalance] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { formatCurrency } = useUserPreferences();

  const fetchSnapshots = async () => {
    try {
      let query = supabase
        .from("balance_snapshots")
        .select("*")
        .order("recorded_at", { ascending: true });

      if (accountId) {
        query = query.eq("account_id", accountId);
      } else if (walletId) {
        query = query.eq("wallet_id", walletId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSnapshots(data || []);
    } catch (error) {
      console.error("Error fetching snapshots:", error);
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      fetchSnapshots();
    }
  }, [dialogOpen, accountId, walletId]);

  const handleAddSnapshot = async () => {
    if (!newBalance) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const snapshotData: any = {
        user_id: user.id,
        balance: parseFloat(newBalance),
        recorded_at: format(selectedDate, "yyyy-MM-dd"),
        notes: notes || null,
      };

      if (accountId) {
        snapshotData.account_id = accountId;
      } else if (walletId) {
        snapshotData.wallet_id = walletId;
      }

      const { error } = await supabase
        .from("balance_snapshots")
        .upsert(snapshotData, { 
          onConflict: accountId ? "account_id,recorded_at" : "wallet_id,recorded_at"
        });

      if (error) throw error;

      toast({ title: "Success", description: "Balance snapshot recorded" });
      setNewBalance("");
      setNotes("");
      setSelectedDate(new Date());
      setAddDialogOpen(false);
      fetchSnapshots();
    } catch (error) {
      console.error("Error adding snapshot:", error);
      toast({ 
        title: "Error", 
        description: getErrorMessage(error) || "Failed to record snapshot", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSnapshot = async (id: string) => {
    try {
      const { error } = await supabase
        .from("balance_snapshots")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast({ title: "Deleted", description: "Snapshot removed" });
      fetchSnapshots();
    } catch (error) {
      console.error("Error deleting snapshot:", error);
    }
  };

  // Calculate growth stats
  const chartData = snapshots.map(s => ({
    date: format(new Date(s.recorded_at), "MMM d"),
    balance: s.balance,
    fullDate: s.recorded_at,
  }));

  // Add current balance as the latest point
  const today = format(new Date(), "yyyy-MM-dd");
  const hasToday = snapshots.some(s => s.recorded_at === today);
  if (!hasToday && chartData.length > 0) {
    chartData.push({
      date: "Today",
      balance: currentBalance,
      fullDate: today,
    });
  }

  const firstBalance = snapshots[0]?.balance || currentBalance;
  const growthAmount = currentBalance - firstBalance;
  const growthPercent = firstBalance > 0 ? ((currentBalance - firstBalance) / firstBalance) * 100 : 0;
  const isPositive = growthAmount >= 0;

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
          <History className="h-3 w-3" />
          History
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Balance History - {accountName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stats Summary */}
          {snapshots.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <p className="text-xs text-muted-foreground">Current</p>
                <p className="font-mono font-bold">{currencySymbol}{currentBalance.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <p className="text-xs text-muted-foreground">Change</p>
                <p className={cn(
                  "font-mono font-bold flex items-center justify-center gap-1",
                  isPositive ? "text-income-text" : "text-expense-text"
                )}>
                  {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {currencySymbol}{Math.abs(growthAmount).toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <p className="text-xs text-muted-foreground">Growth</p>
                <p className={cn(
                  "font-mono font-bold",
                  isPositive ? "text-income-text" : "text-expense-text"
                )}>
                  {isPositive ? "+" : ""}{growthPercent.toFixed(2)}%
                </p>
              </div>
            </div>
          )}

          {/* Chart */}
          {chartData.length > 1 ? (
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }} 
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }} 
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${currencySymbol}${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value: number) => [`${currencySymbol}${value.toLocaleString()}`, "Balance"]}
                    labelFormatter={(label) => label}
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="balance" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    fill="url(#balanceGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-muted-foreground text-sm border border-dashed rounded-lg">
              Add at least 2 snapshots to see the chart
            </div>
          )}

          {/* Add Snapshot Button */}
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full border-dashed">
                <Plus className="h-4 w-4 mr-2" />
                Record Balance Snapshot
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[350px]">
              <DialogHeader>
                <DialogTitle>Record Balance</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(selectedDate, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="snapshotBalance">Balance</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">
                      {currencySymbol}
                    </span>
                    <Input
                      id="snapshotBalance"
                      type="number"
                      step="0.01"
                      placeholder={currentBalance.toString()}
                      className="pl-8 font-mono"
                      value={newBalance}
                      onChange={(e) => setNewBalance(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="snapshotNotes">Notes (optional)</Label>
                  <Input
                    id="snapshotNotes"
                    placeholder="e.g., Monthly update"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
                <Button onClick={handleAddSnapshot} disabled={loading || !newBalance} className="w-full">
                  {loading ? "Saving..." : "Save Snapshot"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Snapshot History List */}
          {snapshots.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              <p className="text-xs text-muted-foreground font-medium">Recent Snapshots</p>
              {[...snapshots].reverse().slice(0, 5).map((snapshot) => (
                <div 
                  key={snapshot.id}
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/30 text-sm group"
                >
                  <div>
                    <span className="font-mono">{currencySymbol}{snapshot.balance.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-2">
                      {format(new Date(snapshot.recorded_at), "MMM d, yyyy")}
                    </span>
                    {snapshot.notes && (
                      <span className="text-muted-foreground/60 ml-2 text-xs">• {snapshot.notes}</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDeleteSnapshot(snapshot.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
